'use client';
import AccountIcon from '@repo/ui/src/components/Icons/AccountIcon';
import useTranslation from '@components/Translation/useTranslation';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import { useCustomer } from '@hooks/customer';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { classNames } from '@repo/ui/src/utils/classNames';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import Image from '@repo/ui/src/components/Image';

interface Props {
  dark?: boolean;
  showTextOnSmall?: boolean;
  full?: boolean;
  loggedOutText?: string;
  loggedInLabel?: string;
  className?: string;
}

const UserAvatar = ({
  dark,
  full,
  loggedOutText = 'Log In or Sign Up',
  loggedInLabel = 'My Account',
  showTextOnSmall,
  className,
}: Props) => {
  const { accountsEnabled } = useCurrentUrl();
  const { data } = useCustomer();
  const { push } = useModal(ModalID.login);
  const { t } = useTranslation();

  if (!accountsEnabled) {
    return null;
  }

  return (
    <Button
      as={data && Anchor}
      aria-label={loggedOutText}
      label={data && loggedInLabel}
      link={data && t('accountLink.link', undefined, { default: '/account/order-history' })}
      variant={ButtonVariant.bare}
      size="none"
      full={full}
      onClick={!data ? () => push() : undefined}
      className={classNames(
        dark ? 'tw-text-surface-800' : 'tw-text-text-100',
        'tw-items-center tw-gap-2 tra-text-web-sub-2 hidden sm:tw-flex tw-p-3 tw-cursor-pointer tw-group',
        className,
      )}
    >
      {data ? (
        <div
          className={classNames(
            dark && 'tw-bg-surface-300 tw-border tw-border-surface-300 tw-rounded-full',
            'tw-relative tw-w-6 tw-h-6',
          )}
        >
          <Image
            className="tw-rounded-full"
            src={data?.avatar ?? '/icons/account.svg'}
            alt=""
            role="presentation"
            layout="fill"
          />
        </div>
      ) : (
        <span>
          <AccountIcon className="tw-w-6" />
        </span>
      )}
      {showTextOnSmall && !data && <span className="tw-block">{loggedOutText}</span>}
    </Button>
  );
};

export default UserAvatar;
